import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Pause, SpeakerHigh, SpeakerSlash, MapPin, Heart, ChatCircle, PaperPlaneTilt, BookmarkSimple, Flag, CaretUp, CaretDown, CaretLeft, CaretRight, Eye } from "@phosphor-icons/react";
import FollowButton from "./FollowButton";
import { toAbsolute, reelsApi } from "@/lib/apiClient";

// Stable guest session ID stored in sessionStorage so unauthenticated views
// are also deduplicated server-side across page refreshes within the same tab.
function getSessionId() {
    const key = "_reel_sid";
    let sid = sessionStorage.getItem(key);
    if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem(key, sid);
    }
    return sid;
}

export default function ReelModal({ reel, user, onClose, onLike, onSave, onShare, onOpenComments, onReport, onPrev, onNext }) {
    const [muted, setMuted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [progress, setProgress] = useState(0);
    const [showIcon, setShowIcon] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [localViews, setLocalViews] = useState(reel.views ?? 0);
    const videoRef = useRef(null);
    const iconTimer = useRef(null);
    const scrollTimeout = useRef(null);
    const viewTimer = useRef(null);
    const viewFired = useRef(false);
    
    // Handle array of media or fallback to single media
    const mediaItems = reel.media && reel.media.length > 0 
        ? reel.media 
        : [{ url: reel.media_url, kind: reel.kind }];
    const currentMedia = mediaItems[currentIndex] || mediaItems[0];

    // Support image rendering if kind is image or extension is image
    const isImage = currentMedia?.kind === 'image' || (!currentMedia?.url?.includes('.mp4') && currentMedia?.url?.match(/\.(jpeg|jpg|gif|png)$/i));

    // Reset view tracking whenever the reel changes
    useEffect(() => {
        setLocalViews(reel.views ?? 0);
        viewFired.current = false;
    }, [reel.id, reel.views]);

    // Auto-play on open and reset progress
    useEffect(() => {
        setProgress(0);
        const v = videoRef.current;
        if (v && !isImage) { 
            v.currentTime = 0;
            v.play().catch(() => {}); 
        }
        return () => { v?.pause(); };
    }, [reel.id, isImage, currentIndex]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => { 
            if (e.key === "Escape") onClose(); 
            if (e.key === "ArrowUp" && onPrev) { e.preventDefault(); onPrev(); }
            if (e.key === "ArrowDown" && onNext) { e.preventDefault(); onNext(); }
            if (e.key === "ArrowLeft" && mediaItems.length > 1) {
                e.preventDefault();
                setCurrentIndex((i) => Math.max(0, i - 1));
            }
            if (e.key === "ArrowRight" && mediaItems.length > 1) {
                e.preventDefault();
                setCurrentIndex((i) => Math.min(mediaItems.length - 1, i + 1));
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose, onPrev, onNext, mediaItems.length]);

    const handleWheel = (e) => {
        if (scrollTimeout.current) return;
        if (e.deltaY < -40 && onPrev) {
            onPrev();
            scrollTimeout.current = setTimeout(() => { scrollTimeout.current = null; }, 800);
        } else if (e.deltaY > 40 && onNext) {
            onNext();
            scrollTimeout.current = setTimeout(() => { scrollTimeout.current = null; }, 800);
        }
    };

    const togglePlay = () => {
        if (isImage) return;
        const v = videoRef.current;
        if (!v) return;
        v.paused ? v.play().catch(() => {}) : v.pause();
        setShowIcon(true);
        clearTimeout(iconTimer.current);
        iconTimer.current = setTimeout(() => setShowIcon(false), 800);
    };

    const handleSeek = (e) => {
        if (isImage) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        const v = videoRef.current;
        if (v && v.duration) {
            v.currentTime = pos * v.duration;
            setProgress(pos * 100);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] flex items-center justify-center p-4 md:p-8"
            style={{ background: "rgba(3,7,15,0.92)", backdropFilter: "blur(16px)" }}
            onClick={onClose}
            data-testid="reel-modal"
        >
            <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-5xl flex flex-col md:flex-row rounded-[28px] overflow-hidden relative"
                style={{ background: "var(--navy)", border: "1px solid rgba(255,255,255,0.08)", maxHeight: "92vh" }}
            >
                {/* Left Column: Creator info */}
                <div className="md:w-64 shrink-0 p-6 flex flex-col gap-4 border-b md:border-b-0 md:border-r relative" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                    <button onClick={onClose} className="self-end w-8 h-8 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.07)", color: "white" }}>
                        ×
                    </button>
                    <Link to={`/profile/${reel.user_handle}`} onClick={onClose} className="flex items-center gap-3 pb-2">
                        <div className="relative shrink-0">
                            <img
                                src={reel.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${reel.user_handle}`}
                                alt=""
                                className="w-14 h-14 rounded-full object-cover"
                                style={{ border: "2px solid rgba(212,175,55,0.5)" }}
                            />
                            {user?.id !== reel.user_id && (
                                <FollowButton handle={reel.user_handle} variant="overlay" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="text-white font-semibold text-sm truncate">@{reel.user_handle}</div>
                            <div className="text-white/50 text-xs truncate">{reel.user_name}</div>
                        </div>
                    </Link>
                    {(reel.location || reel.country) && (
                        <div className="flex items-center gap-2 text-sm text-white/60">
                            <MapPin size={14} color="#D4AF37" className="shrink-0" />
                            <span className="truncate">{reel.location || reel.country}</span>
                        </div>
                    )}
                    {reel.caption && (
                        <p className="text-white/75 text-sm leading-relaxed overflow-y-auto">{reel.caption}</p>
                    )}
                    {reel.recommendation_reason && (
                        <div className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full self-start" style={{ background: "rgba(212,175,55,0.12)", color: "var(--gold)" }}>
                            {reel.recommendation_reason}
                        </div>
                    )}
                </div>

                {/* Center Column: Video / Image */}
                <div className="flex-1 relative bg-black overflow-hidden" style={{ minHeight: 480, maxHeight: "92vh" }} onWheel={handleWheel}>
                    {/* Blurred Dynamic Background */}
                    <div className="absolute inset-0 z-0">
                        {isImage ? (
                            <img 
                                src={toAbsolute(currentMedia?.url)} 
                                alt="" 
                                className="w-full h-full object-cover opacity-60"
                                style={{ filter: "blur(40px) brightness(0.6)" }} 
                            />
                        ) : (
                            <video
                                src={toAbsolute(currentMedia?.url)}
                                poster={toAbsolute(reel.poster_url) || undefined}
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="w-full h-full object-cover opacity-60 pointer-events-none"
                                style={{ filter: "blur(40px) brightness(0.6)" }}
                            />
                        )}
                    </div>
                    
                    {/* Main Content */}
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        {isImage ? (
                            <img 
                                src={toAbsolute(currentMedia?.url)} 
                                alt="" 
                                className="w-full h-full object-contain" 
                            />
                        ) : (
                            <>
                                <video
                                    ref={videoRef}
                                    src={toAbsolute(currentMedia?.url)}
                                    poster={toAbsolute(reel.poster_url) || undefined}
                                    muted={muted}
                                    loop
                                    playsInline
                                    className="w-full h-full object-contain"
                                    onPlay={() => {
                                        setIsPlaying(true);
                                        // Fire view after 2 seconds of actual playback (reels only)
                                        if (!viewFired.current && !reel.is_post) {
                                            clearTimeout(viewTimer.current);
                                            viewTimer.current = setTimeout(() => {
                                                if (!viewFired.current && !reel.is_post) {
                                                    viewFired.current = true;
                                                    reelsApi.view(reel.id, {
                                                        session_id: getSessionId(),
                                                        watch_ms: 2000,
                                                        completed: false,
                                                        source: "modal",
                                                    }).then(() => {
                                                        setLocalViews((v) => v + 1);
                                                    }).catch(() => {});
                                                }
                                            }, 2000);
                                        }
                                    }}
                                    onPause={() => {
                                        setIsPlaying(false);
                                        clearTimeout(viewTimer.current);
                                    }}
                                    onEnded={() => {
                                        // Also record a completed view when the video finishes (reels only)
                                        if (!viewFired.current && !reel.is_post) {
                                            viewFired.current = true;
                                            reelsApi.view(reel.id, {
                                                session_id: getSessionId(),
                                                completed: true,
                                                source: "modal",
                                            }).then(() => {
                                                setLocalViews((v) => v + 1);
                                            }).catch(() => {});
                                        }
                                    }}
                                    onTimeUpdate={(e) => {
                                        const v = e.target;
                                        if (v.duration) setProgress((v.currentTime / v.duration) * 100);
                                    }}
                                    data-testid={`reel-modal-video-${reel.id}`}
                                />
                                {/* Tap-to-play */}
                                <div className="absolute inset-0 cursor-pointer" style={{ zIndex: 2 }} onClick={togglePlay} />
                                {/* Center icon flash */}
                                {showIcon && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 3 }}>
                                        <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}>
                                            {isPlaying
                                                ? <Pause size={36} color="white" weight="fill" />
                                                : <Play size={36} color="white" weight="fill" style={{ marginLeft: 4 }} />}
                                        </div>
                                    </div>
                                )}
                                {/* Progress bar with seek area */}
                                <div 
                                    className="absolute bottom-0 left-0 right-0 cursor-pointer flex items-end group" 
                                    style={{ height: 24, zIndex: 4 }}
                                    onPointerDown={handleSeek}
                                >
                                    <div className="w-full" style={{ height: 4, background: "rgba(255,255,255,0.15)" }}>
                                        <div style={{ width: `${progress}%`, height: "100%", background: "var(--gold)", transition: "width 0.1s linear" }} />
                                    </div>
                                </div>
                                {/* Mute button */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
                                    className="absolute bottom-7 right-3 w-10 h-10 rounded-full grid place-items-center"
                                    style={{ background: "rgba(0,0,0,0.6)", zIndex: 5 }}
                                    data-testid="reel-modal-mute"
                                >
                                    {muted ? <SpeakerSlash size={16} color="white" /> : <SpeakerHigh size={16} color="#D4AF37" />}
                                </button>
                            </>
                        )}
                    </div>
                    
                    {/* Carousel Nav for multiple media */}
                    {mediaItems.length > 1 && (
                        <>
                            <button
                                aria-label="Prev Image"
                                onClick={(e) => { e.stopPropagation(); setCurrentIndex((i) => Math.max(0, i - 1)); }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                                style={{ background: "rgba(0,0,0,0.4)", color: "white", zIndex: 20 }}
                                disabled={currentIndex === 0}
                            >
                                <CaretLeft size={20} weight="bold" />
                            </button>
                            <button
                                aria-label="Next Image"
                                onClick={(e) => { e.stopPropagation(); setCurrentIndex((i) => Math.min(mediaItems.length - 1, i + 1)); }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                                style={{ background: "rgba(0,0,0,0.4)", color: "white", zIndex: 20, marginRight: (onPrev || onNext) ? '3.5rem' : '0' }}
                                disabled={currentIndex === mediaItems.length - 1}
                            >
                                <CaretRight size={20} weight="bold" />
                            </button>
                            <div className="absolute top-4 right-4 text-xs px-2 py-1 rounded-full" style={{ background: "rgba(0,0,0,0.5)", color: "white", zIndex: 20 }}>
                                {currentIndex + 1}/{mediaItems.length}
                            </div>
                        </>
                    )}

                    {/* Scroll Overlay Nav */}
                    {(onPrev || onNext) && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 pointer-events-none" style={{ zIndex: 20 }}>
                            {onPrev && (
                                <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="w-10 h-10 rounded-full flex items-center justify-center pointer-events-auto hover:scale-110 transition-transform" style={{ background: "rgba(0,0,0,0.4)", color: "white" }}>
                                    <CaretUp size={20} weight="bold" />
                                </button>
                            )}
                            {onNext && (
                                <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="w-10 h-10 rounded-full flex items-center justify-center pointer-events-auto hover:scale-110 transition-transform" style={{ background: "rgba(0,0,0,0.4)", color: "white" }}>
                                    <CaretDown size={20} weight="bold" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Right Column: Actions */}
                <div className="md:w-20 shrink-0 flex md:flex-col items-center justify-around md:justify-center gap-6 p-4 border-t md:border-t-0 md:border-l" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                    {!reel.is_post && (
                        <ModalAction
                            icon={<Eye size={22} />}
                            label={localViews}
                            testId={`reel-modal-views-${reel.id}`}
                        />
                    )}
                    <ModalAction
                        icon={<Heart size={22} weight={reel.liked ? "fill" : "regular"} color={reel.liked ? "#ff7070" : "white"} />}
                        label={reel.likes || 0}
                        onClick={onLike}
                        testId={`reel-modal-like-${reel.id}`}
                    />
                    <ModalAction
                        icon={<ChatCircle size={22} />}
                        label={reel.comments_count || 0}
                        onClick={onOpenComments}
                        testId={`reel-modal-comments-${reel.id}`}
                    />
                    <ModalAction
                        icon={<PaperPlaneTilt size={22} />}
                        label={reel.shares || 0}
                        onClick={onShare}
                        testId={`reel-modal-share-${reel.id}`}
                    />
                    <ModalAction
                        icon={<BookmarkSimple size={22} weight={reel.saved ? "fill" : "regular"} color={reel.saved ? "#D4AF37" : "white"} />}
                        label={reel.saves || 0}
                        onClick={onSave}
                        testId={`reel-modal-save-${reel.id}`}
                    />
                    <ModalAction
                        icon={<Flag size={22} />}
                        label="Report"
                        onClick={onReport}
                        testId={`reel-modal-report-${reel.id}`}
                    />
                </div>
            </motion.div>
        </motion.div>
    );
}

export function ModalAction({ icon, label, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            className="flex flex-col items-center gap-1 text-white/75 hover:text-white transition-colors"
            data-testid={testId}
        >
            {icon}
            <span className="text-[10px]">{label}</span>
        </button>
    );
}
